/**
 * Service-location context (DNKADM-0033 U1; re-pointed to owned locations in
 * DNKADM-0051 U1).
 *
 * Holds the operator's **selected location** — the location id that scopes the
 * **Operations** screens whose dink-api endpoints accept one. Since DNKADM-0051
 * the source is the **owned `v2_location`** list (`listLocations()`,
 * `src/api/locations.ts`, handoff §5.2/§4.6) — a real owned `v2_location.id`, no
 * longer the v1 GHL service-locations proxy. The owned location list is mapped into
 * this context's existing `{ id, name, isActive, connectionStatus }` consumption
 * shape **without changing the public selector contract** (`connectionStatus` is
 * `null` for owned locations — owned-location payment health is the §5.16 job).
 *
 * **Public contract unchanged.** `useServiceLocation()` still returns the same
 * `{ locations, selectedId, selected, setSelectedId, status, retry }`; consumers
 * read `selectedId` + `status` exactly as before. Only the data SOURCE changed
 * (owned ids instead of GHL ids), per the spec's "re-point the selector without
 * changing its public contract".
 *
 * The owned list endpoint degrades an empty body to `[]` (a first-run empty tenant
 * is a real `ready` state, §8), but a thrown `ApiError` (upstream/network/`403`)
 * still lands the context in `error` so a consumer that *requires* a location
 * renders an error+Retry instead of fetching with a garbage id.
 *
 * **Default selection.** On first load with no persisted choice, the first
 * `isActive` location (falling back to the first location when none flag active,
 * or none carry an `isActive` flag). A persisted `selectedId` that is no longer in
 * the list falls back to that default.
 *
 * **Persistence.** The selected `id` is a **non-secret UI preference** stored in
 * `localStorage`, keyed **per tenant slug** so switching tenants can't
 * cross-contaminate the choice. It is NOT a token and does not touch the Option-A
 * in-memory-token invariant. Read on mount.
 *
 * **Scope.** The provider mounts **inside the authenticated shell** (`AppLayout`,
 * which only renders once `AuthGate` has a session), wrapping the routed
 * Operations content so the app-bar `LocationChooser` and Operations screens can
 * read it. The **Operations-only** rule is the consumer's contract (DNKADM-0033
 * U2): **Configuration / Hours of Operation keeps its own per-screen selector** and
 * does NOT read this context.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { listLocations, type V2Location } from '@/api/locations';
import type { ServiceLocation } from '@/api/operatingHours';
import { useCurrentOperator } from '@/auth/authHooks';
import {
  ServiceLocationContext,
  type LocationStatus,
  type SelectedServiceLocation,
  type ServiceLocationContextValue,
} from './locationContextValue';

/**
 * Map an owned `v2_location` into this context's `ServiceLocation` consumption
 * shape (DNKADM-0051 U1). The owned id/name/isActive carry straight across; owned
 * locations have no payment-connection health on this surface, so
 * `connectionStatus` is `null` (the chooser then renders no dot — owned-location
 * payment health is §5.16's job). The owned record's IANA `timezone` is carried
 * across (DNKADM-0210 U2 — the venue clock the reservations grid positions on); a
 * blank one degrades to `null` so consumers fail soft. Keeps the selector's public
 * contract unchanged while sourcing from owned ids.
 */
function toServiceLocation(loc: V2Location): ServiceLocation {
  return {
    id: loc.id,
    name: loc.name === '' ? null : loc.name,
    isActive: loc.isActive,
    connectionStatus: null,
    timezone: loc.timezone.trim() === '' ? null : loc.timezone,
  };
}

/** localStorage key for the selected location, namespaced per tenant slug. */
function storageKey(tenantSlug: string | undefined): string {
  return `dink_admin.serviceLocation.${tenantSlug ?? 'unknown'}`;
}

/** Read the persisted selected id for a tenant (defaults to null on any failure). */
function readPersisted(tenantSlug: string | undefined): string | null {
  try {
    return localStorage.getItem(storageKey(tenantSlug));
  } catch {
    return null;
  }
}

/** Persist (or clear) the selected id for a tenant; ignores quota/disabled storage. */
function writePersisted(tenantSlug: string | undefined, id: string | null): void {
  try {
    if (id) localStorage.setItem(storageKey(tenantSlug), id);
    else localStorage.removeItem(storageKey(tenantSlug));
  } catch {
    /* ignore quota / disabled storage — the in-memory selection still governs the UI */
  }
}

/**
 * The default selection for a freshly loaded list: the first `isActive` location,
 * else the first location, else null (empty list). `isActive: null` (no usable
 * flag) is treated as "not known active", so a list with no flags falls through to
 * the first location.
 */
function defaultSelectedId(locations: ServiceLocation[]): string | null {
  const firstActive = locations.find((l) => l.isActive === true);
  if (firstActive) return firstActive.id;
  return locations[0]?.id ?? null;
}

export interface LocationProviderProps {
  children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug;

  const [locations, setLocations] = useState<ServiceLocation[]>([]);
  const [selectedId, setSelectedIdState] = useState<string | null>(null);
  const [status, setStatus] = useState<LocationStatus>('loading');
  const [error, setError] = useState<unknown>(null);

  // Guards against committing a stale in-flight load (last-call-wins) and against
  // setting state after unmount, mirroring useResource.
  const mountedRef = useRef(true);
  const runIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setStatus('loading');
    setError(null);
    void (async () => {
      try {
        const owned = await listLocations();
        if (!mountedRef.current || runIdRef.current !== runId) return;
        const list = owned.map(toServiceLocation);
        setLocations(list);
        // Resolve the selection: a valid persisted choice wins, else the default.
        const persisted = readPersisted(tenantSlug);
        const resolved =
          persisted && list.some((l) => l.id === persisted)
            ? persisted
            : defaultSelectedId(list);
        setSelectedIdState(resolved);
        // Reconcile storage so a stale/absent persisted id is replaced by the default.
        writePersisted(tenantSlug, resolved);
        setStatus('ready');
      } catch (err) {
        if (!mountedRef.current || runIdRef.current !== runId) return;
        // Fail-closed: no selectable location; consumers that require one show error+Retry.
        setLocations([]);
        setSelectedIdState(null);
        setError(err);
        setStatus('error');
      }
    })();
  }, [tenantSlug]);

  // Load on mount and whenever the tenant changes (a different tenant => a different
  // location set and a different persisted key).
  useEffect(() => {
    load();
  }, [load]);

  const setSelectedId = useCallback(
    (id: string) => {
      setLocations((current) => {
        // Ignore an id that isn't in the loaded set (never select a garbage id).
        if (!current.some((l) => l.id === id)) return current;
        setSelectedIdState(id);
        writePersisted(tenantSlug, id);
        return current;
      });
    },
    [tenantSlug],
  );

  const selected = useMemo<SelectedServiceLocation | null>(() => {
    if (selectedId === null) return null;
    const match = locations.find((l) => l.id === selectedId);
    return match ? { id: match.id, name: match.name } : null;
  }, [locations, selectedId]);

  const value = useMemo<ServiceLocationContextValue>(
    () => ({ locations, selectedId, selected, setSelectedId, status, error, retry: load }),
    [locations, selectedId, selected, setSelectedId, status, error, load],
  );

  return (
    <ServiceLocationContext.Provider value={value}>
      {children}
    </ServiceLocationContext.Provider>
  );
}
